/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AnimatedButton from '@/components/animated-button';
import { FloatingLabelInput } from '@/components/ui/floating-label-input';
import { isMsg91OtpLimitError } from '@/lib/auth/msg91-validation';

const RESEND_COOLDOWN_SECONDS = 30;

// MSG91 errors meaning the attempt limit for this OTP is used up (e.g. "verification limit exceeded")
function formatTimer(seconds: number): string {
    const mm = Math.floor(seconds / 60)
        .toString()
        .padStart(2, '0');
    const ss = (seconds % 60).toString().padStart(2, '0');

    return `${mm}:${ss}`;
}

interface Msg91OtpPopupProps {
    isOpen: boolean;
    maskedIdentifier: string;
    otpLength?: number;
    isVerifying: boolean;
    error?: string | null;
    onClose: () => void;
    onEdit: () => void;
    onVerify: (code: string) => void;
    onResend: () => void;
    /** 'signIn' for an already-registered identifier, 'signUp' for a new one. */
    mode?: 'signIn' | 'signUp';
}

export default function Msg91OtpPopup({
    isOpen,
    maskedIdentifier,
    otpLength = 4,
    isVerifying,
    error,
    onClose,
    onEdit,
    onVerify,
    onResend,
    mode = 'signUp',
}: Msg91OtpPopupProps): ReactElement {
    const { t } = useTranslation('login');
    const actionLabel = mode === 'signIn' ? t('signIn') : t('signUp');

    const [code, setCode] = useState('');
    const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS);
    // Once the attempt limit is hit, verify and resend stay locked until the popup is reopened
    // (i.e. the shopper goes back and requests a fresh OTP, which MSG91 rate-limits on its side)
    const [isAttemptLimitReached, setIsAttemptLimitReached] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const otpPlaceholder = 'OTP';

    useEffect(() => {
        if (isOpen) {
            setCode('');
            setResendTimer(RESEND_COOLDOWN_SECONDS);
            setIsAttemptLimitReached(false);

            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [isOpen]);

    useEffect(() => {
        if (isMsg91OtpLimitError(error)) {
            setIsAttemptLimitReached(true);
            setCode('');
        }
    }, [error]);

    useEffect(() => {
        if (!isOpen || resendTimer <= 0 || isAttemptLimitReached) return;

        const timer = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);

        return () => clearTimeout(timer);
    }, [isOpen, resendTimer]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (code.trim().length !== otpLength || isVerifying || isAttemptLimitReached) return;

        onVerify(code.trim());
    };

    const handleResend = () => {
        if (resendTimer > 0 || isAttemptLimitReached) return;

        setResendTimer(RESEND_COOLDOWN_SECONDS);
        setCode('');
        onResend();
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                data-testid="msg91-otp-popup"
                className="!rounded-lg h-auto min-h-[339px] w-[calc(100%-40px)] max-w-[calc(100%-40px)] content-start gap-5 overflow-hidden bg-background px-5 pt-10 pb-10 sm:max-w-[calc(100%-40px)] md:min-h-[446px] md:w-[390px] md:max-w-[390px] md:gap-2 md:px-6 md:pt-16 md:pb-16 [&>button]:cursor-pointer">
                <DialogHeader className="items-start gap-2 text-left md:gap-3">
                    <DialogTitle className="!font-bold font-georama text-[31px] leading-[1.1] tracking-tight uppercase [text-shadow:0_0_0.4px_currentColor]">
                        {actionLabel}
                    </DialogTitle>

                    <DialogDescription className="font-lato text-left text-sm leading-5 font-normal">
                        {t('msg91OtpDescription', {
                            otpLength,
                            identifier: maskedIdentifier,
                        })}
                    </DialogDescription>

                    <button
                        type="button"
                        onClick={onEdit}
                        className="font-lato w-fit text-base leading-5 font-normal text-foreground hover:text-foreground/80">
                        <span className="border-b border-foreground pb-0.5">{t('editIdentifier')}</span>
                    </button>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="mt-0 flex flex-col md:mt-4 md:gap-3">
                    <div className="mb-1 md:mb-0">
                        <FloatingLabelInput
                            id="msg91-otp-code"
                            ref={inputRef}
                            label={otpPlaceholder}
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="[0-9]*"
                            maxLength={otpLength}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                            disabled={isVerifying || isAttemptLimitReached}
                            status={error && error.trim() !== '' ? 'error' : 'default'}
                            containerClassName="rounded-md border-foreground"
                        />
                    </div>

                    {error && error.trim() !== '' && (
                        <p className="text-destructive font-lato text-xs leading-[18px] font-normal tracking-normal">
                            {error}
                        </p>
                    )}

                    {!isAttemptLimitReached && (
                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={resendTimer > 0}
                            className="font-lato mb-5 flex w-fit items-baseline gap-1 text-base leading-5 font-normal text-foreground disabled:cursor-not-allowed disabled:text-muted-foreground md:mb-0">
                            <span className="border-b border-foreground pb-0.5">{t('resendOtp')}</span>

                            {resendTimer > 0 && (
                                <span className="text-[10px] leading-4 font-normal">
                                    {t('resendOtpTimerSuffix', {
                                        timer: formatTimer(resendTimer),
                                    })}
                                </span>
                            )}
                        </button>
                    )}

                    <AnimatedButton
                        type="submit"
                        disabled={code.length !== otpLength || isVerifying || isAttemptLimitReached}
                        variant="secondary"
                        icon={<ArrowRight className="size-6" />}
                        className="!mt-0 h-[52px] w-full gap-3 !rounded-full text-base leading-[22px] font-normal md:!mt-1"
                    >
                        {isVerifying ? t('verifying') : actionLabel}
                    </AnimatedButton>
                </form>
            </DialogContent>
        </Dialog>
    );
}
